import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, CalendarClock, MessageCircle, MapPin, ArrowUpRight, Plus, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { api } from "@/lib/api";

const CHART_COLORS = ["#1F5C44", "#2E7D5B", "#4E9E78", "#7FBF9F", "#A9D6BE", "#CDE7D8"];

function StatCard({ label, value, icon: Icon, hint, gold }) {
  return (
    <div className="c-card border rounded-xl p-6 card-elev card-elev-hover" data-testid="stat-card">
      <div className="flex items-start justify-between mb-4">
        <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${gold ? "bg-emerald2/10 text-emerald2 dark:bg-emerald2/20 dark:text-[#7FBF9F]" : "bg-ink/[0.04] text-ink dark:bg-white/[0.06] dark:text-[#ECE9E2]"}`}>
          <Icon className="w-5 h-5" strokeWidth={1.6} />
        </div>
        {hint}
      </div>
      <div className="font-ui text-3xl sm:text-4xl font-semibold c-strong leading-none tabular-nums tracking-tight">{value}</div>
      <div className="text-[11px] uppercase tracking-[0.16em] c-muted mt-3 font-medium">{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(false);
  const [botOnline, setBotOnline] = useState(false);

  useEffect(() => {
    api.get("/stats")
      .then((r) => setStats(r.data))
      .catch(() => setStatsError(true));

    // Check bot status every 15 seconds
    const checkBot = () => {
      api.get("/bot/status").then((r) => setBotOnline(r.data.online)).catch(() => setBotOnline(false));
    };
    checkBot();
    const interval = setInterval(checkBot, 15000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="p-4 pt-16 sm:p-8 lg:p-12 max-w-6xl mx-auto animate-fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8 sm:mb-10">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-gold mb-2">Beranda</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight c-strong">Arsip Digital Notaris</h1>
          <p className="text-sm c-muted mt-2">Ringkasan brankas dokumen Anda hari ini.</p>
        </div>
        <Link to="/upload" data-testid="dashboard-upload-cta" className="bg-gold hover:bg-gold-dark text-white px-4 sm:px-5 py-2.5 sm:py-3 rounded-lg text-sm font-medium transition-all flex items-center gap-2 shadow-lg shadow-gold/25">
          <Plus className="w-4 h-4" strokeWidth={2} /> Upload
        </Link>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-5 mb-5 stagger">
        <StatCard label="Total Akta" value={stats?.total ?? "—"} icon={FileText} gold
          hint={<span className="flex items-center gap-1 text-xs text-emerald2 dark:text-[#7FBF9F]"><TrendingUp className="w-3.5 h-3.5" /> arsip</span>} />
        <StatCard label="Akta Bulan Ini" value={stats?.this_month ?? "—"} icon={CalendarClock} />
        <StatCard
          label="Status Bot WhatsApp"
          value={<span className="text-xl sm:text-3xl">{botOnline ? "Online" : "Offline"}</span>}
          icon={MessageCircle}
          hint={botOnline
            ? <span className="flex items-center gap-2 text-xs text-emerald2 dark:text-[#7FBF9F]"><span className="w-2 h-2 rounded-full bg-emerald2 dark:bg-[#7FBF9F] pulse-dot" /> online</span>
            : <span className="flex items-center gap-2 text-xs text-wine dark:text-[#E08585]"><span className="w-2 h-2 rounded-full bg-wine dark:bg-[#E08585] pulse-dot" /> offline</span>
          }
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-5">

        <div className="lg:col-span-2 c-card border rounded-xl p-6 card-elev">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <div>
              <div className="font-display text-xl sm:text-2xl font-medium c-strong">Distribusi Jenis Akta</div>
              <div className="text-xs c-muted mt-0.5 hidden sm:block">Komposisi arsip berdasarkan jenis dokumen</div>
            </div>
          </div>
          {stats?.by_type?.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={stats.by_type} layout="vertical" margin={{ left: 0, right: 16 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: "#8A8F96", fontFamily: "Outfit" }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "rgba(127,191,159,0.10)" }} contentStyle={{ borderRadius: 10, border: "1px solid rgba(127,127,127,0.25)", fontSize: 12, fontFamily: "Outfit", background: "#1b2228", color: "#eee" }} />
                <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={26}>
                  {stats.by_type.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyMini text="Belum ada data akta." />}
        </div>

        <div className="c-card border rounded-xl p-6 card-elev">
          <div className="font-display text-2xl font-medium c-strong mb-1 flex items-center gap-2"><MapPin className="w-5 h-5 text-gold" strokeWidth={1.6} /> Per Daerah</div>
          <div className="text-xs c-muted mb-5">Sebaran wilayah dokumen</div>
          {stats?.by_region?.length ? (
            <div className="space-y-4">
              {stats.by_region.map((r, i) => {
                const max = stats.by_region[0]?.count || 1;
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="c-text">{r.name}</span>
                      <span className="font-mono c-muted text-xs">{r.count}</span>
                    </div>
                    <div className="h-1.5 c-soft rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#1F5C44] to-[#4E9E78] rounded-full" style={{ width: `${(r.count / max) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <EmptyMini text="Belum ada daerah." />}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:gap-5 mt-5">
        <div className="c-card border rounded-xl p-6 card-elev">
          <div className="flex items-center justify-between mb-5">
            <div className="font-display text-2xl font-medium c-strong">Dokumen Terbaru</div>
            <Link to="/arsip" className="text-xs text-gold hover:text-gold-dark flex items-center gap-1 font-medium">
              Lihat semua <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          {stats?.recent?.length ? (
            <div className="divide-y c-divide">
              {stats.recent.map((d) => (
                <div key={d._id} className="py-3 sm:py-3.5 flex items-center justify-between group" data-testid="recent-doc-row">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 shrink-0 rounded-lg c-soft border c-border flex items-center justify-center text-gold">
                      <FileText className="w-4 h-4" strokeWidth={1.6} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium c-strong truncate">{d.client_name}</div>
                      <div className="text-xs c-muted truncate">{d.document_type} · {d.region || "—"}</div>
                    </div>
                  </div>
                  <div className="font-mono text-xs c-muted shrink-0 ml-2 hidden sm:block">{d.deed_number || "—"}</div>
                </div>
              ))}
            </div>
          ) : <EmptyMini text="Belum ada dokumen. Mulai dengan mengunggah arsip pertama Anda." />}
        </div>
      </div>
    </div>
  );
}

function EmptyMini({ text }) {
  return <div className="text-sm text-[#A0A0A0] py-10 text-center">{text}</div>;
}
